import { ErrorBoundary } from "@/components/ErrorBoundary";
import PWAInstallPrompt from "@/components/PWAInstallPrompt.web";
import {
  type DefaultHomeTab,
  resolveHomeRoute,
} from "@/constants/AppNavigation";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { useColorScheme } from "@/hooks/useColorScheme";
import {
  DarkTheme,
  DefaultTheme,
  StackActions,
  ThemeProvider,
} from "@react-navigation/native";
import * as Notifications from "expo-notifications";
import {
  Slot,
  Stack,
  router,
  usePathname,
  useRootNavigation,
  useSegments,
} from "expo-router";
import * as SplashScreen from "expo-splash-screen";
import { useCallback, useEffect, useState } from "react";
import { ActivityIndicator, Platform, StyleSheet, View } from "react-native";
import "react-native-reanimated";
import "../utils/i18n";

// Firebase instances (web)
import { auth as webAuth, firestore as webFirestore } from "../firebase/web";

// Modular web SDK helpers
import {
  getRedirectResult as webGetRedirectResult,
  type User as WebUser,
} from "firebase/auth";
import {
  onSnapshot,
  doc as webDoc,
  setDoc as webSetDoc,
} from "firebase/firestore";
import { ensureUserDocExists } from "@/utils/createUserDoc.web";
import { processGroupIdParam } from "@/utils/processGroupIdParam.web";
import {
  hasCompletedInitialOnboardingTour,
  hasFollowedPlate,
  hasMandatoryOnboardingFields,
  isPhoneOnlyAuthUser,
  isPhoneOnlyUser,
} from "../utils/onboarding.web";

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    shouldShowAlert: true,
    shouldPlaySound: true,
    shouldSetBadge: false,
    shouldShowBanner: true,
    shouldShowList: true,
  }),
});

SplashScreen.preventAutoHideAsync();

const isWeb = Platform.OS === "web";
const auth: any = webAuth;
const firestore: any = webFirestore;

const InitialLayout = () => {
  const { user, isLoading } = useAuth();
  const [onboardingState, setOnboardingState] = useState<{
    hasUserDoc: boolean;
    hasMandatoryProfileFields: boolean;
    hasCompletedInitialOnboardingTour: boolean;
    isPhoneOnlyUser: boolean;
    hasFollowedPlate: boolean;
    defaultHomeTab?: DefaultHomeTab;
  } | null>(null);
  const segments = useSegments();
  const pathname = usePathname();
  const colorScheme = useColorScheme();
  const rootNavigation = useRootNavigation();

  useEffect(() => {
    if (!isLoading) {
      SplashScreen.hideAsync();
    }
  }, [isLoading]);

  useEffect(() => {
    let mounted = true;
    let unsubscribe: (() => void) | null = null;

    if (!user) {
      if (mounted) setOnboardingState(null);
      return;
    }

    const userDocRef = webDoc(firestore, "users", user.uid);
    unsubscribe = onSnapshot(
      userDocRef,
      async (userDoc) => {
        if (!mounted) return;

        const userDocData = userDoc.exists() ? userDoc.data() : null;
        if (userDocData) {
          const hasLegacyCompletion =
            hasMandatoryOnboardingFields(userDocData);
          const hasNewCompletionFlag =
            userDocData?.onboardingPrefs?.initialOnboardingCompleted ??
            userDocData?.["onboardingPrefs.initialOnboardingCompleted"];

          if (hasLegacyCompletion && hasNewCompletionFlag === undefined) {
            try {
              await webSetDoc(
                webDoc(firestore, "users", user.uid),
                {
                  "onboardingPrefs.initialOnboardingCompleted": true,
                  "onboardingPrefs.initialOnboardingStepId": null,
                },
                { merge: true },
              );
            } catch (migrationError) {
              console.error(
                "Error migrating legacy onboarding completion:",
                migrationError,
              );
            }
          }
        }

        if (mounted) {
          setOnboardingState({
            hasUserDoc: userDocData !== null,
            hasMandatoryProfileFields:
              hasMandatoryOnboardingFields(userDocData),
            hasCompletedInitialOnboardingTour:
              hasCompletedInitialOnboardingTour(userDocData),
            isPhoneOnlyUser: isPhoneOnlyUser(userDocData),
            hasFollowedPlate: hasFollowedPlate(userDocData),
            defaultHomeTab: userDocData?.defaultHomeTab as
              | DefaultHomeTab
              | undefined,
          });
        }
      },
      (err) => {
        // Do NOT flip hasUserDoc:false here — onSnapshot auto-retries the
        // transport; on transient "client is offline" errors from the
        // Firestore channel (common in installed PWAs / Android WebViews /
        // right after sign-in) we'd otherwise bounce an already-signed-in
        // user in (tabs) back to /(auth)/login. Leaving onboardingState
        // unchanged keeps the router where it is, and the next successful
        // snapshot updates state normally.
        console.error("Error listening to user doc:", err);
      },
    );

    return () => {
      mounted = false;
      if (unsubscribe) unsubscribe();
    };
  }, [user]);

  const handleNotificationResponse = useCallback(
    (response: Notifications.NotificationResponse) => {
      const data = response.notification.request.content.data;
      if (
        (data?.type === "NEW_POST" || data?.type === "GLOBAL_FEED_POST") &&
        data.postId &&
        rootNavigation?.isReady() &&
        user
      ) {
        rootNavigation.dispatch(
          StackActions.push("(tabs)", {
            screen: "post/[postId]",
            params: { postId: data.postId as string },
          }),
        );
      } else if (
        data?.type === "GROUP_ACCESS_REQUEST" &&
        rootNavigation?.isReady() &&
        user
      ) {
        if (data.groupId) {
          rootNavigation.dispatch(
            StackActions.push("(tabs)", {
              screen: "groups/group-info",
              params: { groupId: data.groupId as string },
            }),
          );
        } else {
          rootNavigation.dispatch(
            StackActions.push("(tabs)", { screen: "groups" }),
          );
        }
      } else if (
        data?.type === "GROUP_ACCESS_APPROVED" &&
        rootNavigation?.isReady() &&
        user
      ) {
        if (data.groupId) {
          rootNavigation.dispatch(
            StackActions.push("(tabs)", {
              screen: "groups/[id]",
              params: { id: data.groupId as string },
            }),
          );
        } else {
          rootNavigation.dispatch(
            StackActions.push("(tabs)", { screen: "groups" }),
          );
        }
      }
    },
    [rootNavigation, user],
  );

  useEffect(() => {
    // expo-notifications is not supported on web; skip listeners
    if (Platform.OS === "web") {
      return;
    }
    if (!rootNavigation?.isReady() || !user) {
      return;
    }

    const responseListener =
      Notifications.addNotificationResponseReceivedListener(
        handleNotificationResponse,
      );

    Notifications.getLastNotificationResponseAsync().then((response) => {
      if (response) {
        handleNotificationResponse(response);
      }
    });

    return () => {
      responseListener.remove();
    };
  }, [rootNavigation, handleNotificationResponse, user]);

  useEffect(() => {
    if (isLoading) return;
    const isGuestPublicPath =
      pathname === "/" ||
      pathname === "/index" ||
      pathname.includes("public-profile");

    const phoneOnlyAuthSignal = isPhoneOnlyAuthUser(user);
    const isAuthenticated =
      !!user && (user.emailVerified || phoneOnlyAuthSignal);

    if (isAuthenticated) {
      // signup.web.tsx briefly authenticates as an existing account when a
      // phone signup collides with an already-registered user, then signs
      // out. While that's in flight, skip every redirect so the rejection
      // modal can render on signup instead of bouncing us into (tabs).
      if (
        typeof window !== "undefined" &&
        window.sessionStorage?.getItem("debari:authRedirectBlocked") === "1"
      ) {
        return;
      }
      if (onboardingState === null) return;

      const phoneOnly =
        onboardingState.isPhoneOnlyUser || phoneOnlyAuthSignal;

      const onboardingComplete = phoneOnly
        ? onboardingState.hasFollowedPlate &&
          onboardingState.hasCompletedInitialOnboardingTour
        : onboardingState.hasMandatoryProfileFields &&
          onboardingState.hasCompletedInitialOnboardingTour;

      if (
        onboardingState.hasUserDoc &&
        onboardingComplete &&
        segments[0] !== "(tabs)"
      ) {
        // Fire-and-forget. processGroupIdParam clears the groupId field up
        // front so a re-fire of this effect can't double-write, leaves any
        // other persisted signup-URL params alone for their own
        // processors, and swallows its own errors so a bad groupId never
        // blocks app entry. See utils/processGroupIdParam.web.ts for the
        // full contract and its unit tests.
        if (user) {
          void processGroupIdParam({
            firestore,
            user: user as WebUser,
          });
        }
        router.replace(
          resolveHomeRoute(onboardingState.defaultHomeTab) as any,
        );
        return;
      }

      if (
        onboardingState.hasUserDoc &&
        !onboardingComplete &&
        !(segments[0] === "(auth)" && segments[1] === "onboarding")
      ) {
        router.replace("/(auth)/onboarding" as any);
        return;
      }

      if (!onboardingState.hasUserDoc && segments[0] !== "(auth)") {
        router.push("/(auth)/login");
        return;
      }
    }

    // `app/index.tsx` catches `/` and renders only a spinner — it expects the
    // root layout to route the visitor onward. Guests at `/` belong on the
    // global feed inside the (tabs) group, so bounce them there before the
    // isGuestPublicPath early-return keeps them stuck on the spinner.
    if (!user && pathname === "/") {
      router.replace("/(tabs)" as any);
      return;
    }

    if (!user && isGuestPublicPath) {
      return;
    }

    if (!user && segments[0] !== "(auth)") {
      router.replace("/(auth)/login" as any);
    }
  }, [user, isLoading, onboardingState, segments, pathname]);

  if (isLoading) {
    return <Slot />;
  }

  // Bridge the gap between auth state flipping and the user-doc snapshot
  // arriving: while we wait on onboardingState, the routing effect can't
  // redirect, so the current (auth) route would briefly re-render —
  // visibly flashing the login/signup page after a successful OAuth.
  // We KEEP the Stack mounted (so in-flight handlers in
  // login.web.tsx / signup.web.tsx — e.g. ensureUserDocExists +
  // router.replace — finish without losing PhoneAuthFlow state) and just
  // cover it with an opaque overlay. Scoped to (auth) excluding onboarding
  // so cold loads of (tabs), public pages, and onboarding itself aren't
  // covered. Preserves the debari:authRedirectBlocked phone-signup
  // collision flow.
  const isAuthenticatedAwaitingOnboardingState =
    !!user &&
    (user.emailVerified || isPhoneOnlyAuthUser(user)) &&
    onboardingState === null &&
    segments[0] === "(auth)" &&
    segments[1] !== "onboarding" &&
    !(
      typeof window !== "undefined" &&
      window.sessionStorage?.getItem("debari:authRedirectBlocked") === "1"
    );

  // Determine if we should show a blank state while redirecting
  const isRedirecting =
    // Keep public guest pages rendered; don't blank-screen while evaluating guards.
    (!user &&
      !(pathname === "/" || pathname === "/index") &&
      !pathname.includes("public-profile") &&
      segments[0] !== "(auth)") ||
    (user &&
      (user.emailVerified || isPhoneOnlyAuthUser(user)) &&
      onboardingState &&
      !(
        typeof window !== "undefined" &&
        window.sessionStorage?.getItem("debari:authRedirectBlocked") === "1"
      ) &&
      (() => {
        const phoneOnly =
          onboardingState.isPhoneOnlyUser || isPhoneOnlyAuthUser(user);
        const onboardingComplete = phoneOnly
          ? onboardingState.hasFollowedPlate &&
            onboardingState.hasCompletedInitialOnboardingTour
          : onboardingState.hasMandatoryProfileFields &&
            onboardingState.hasCompletedInitialOnboardingTour;

        return (
          (onboardingState.hasUserDoc &&
            onboardingComplete &&
            segments[0] !== "(tabs)") ||
          (onboardingState.hasUserDoc &&
            !onboardingComplete &&
            !(segments[0] === "(auth)" && segments[1] === "onboarding")) ||
          (!onboardingState.hasUserDoc && segments[0] !== "(auth)")
        );
      })());

  if (isRedirecting) {
    return null;
  }

  const themeValue = colorScheme === "dark" ? DarkTheme : DefaultTheme;

  return (
    <ThemeProvider value={themeValue}>
      <View role="main" nativeID="main-content" style={{ flex: 1 }}>
        <Stack screenOptions={{ headerShown: false }}>
          <Stack.Screen name="(tabs)" options={{ headerShown: false }} />
          <Stack.Screen name="(auth)" options={{ headerShown: false }} />
          <Stack.Screen name="+not-found" />
        </Stack>
      </View>
      {isAuthenticatedAwaitingOnboardingState && (
        <View
          style={[
            StyleSheet.absoluteFillObject,
            {
              backgroundColor:
                colorScheme === "dark"
                  ? DarkTheme.colors.background
                  : DefaultTheme.colors.background,
              alignItems: "center",
              justifyContent: "center",
              zIndex: 9999,
            },
          ]}
          pointerEvents="auto"
        >
          <ActivityIndicator
            size="large"
            color={
              colorScheme === "dark"
                ? DarkTheme.colors.text
                : DefaultTheme.colors.text
            }
          />
        </View>
      )}
      {isWeb && <PWAInstallPrompt />}
    </ThemeProvider>
  );
};

export default function RootLayout() {
  const [redirectChecked, setRedirectChecked] = useState<boolean>(false);

  useEffect(() => {
    // Only run on web
    if (isWeb) {
      // Register service worker manually
      if ("serviceWorker" in navigator) {
        window.addEventListener("load", () => {
          navigator.serviceWorker
            .register("/firebase-messaging-sw.js")
            .then((registration) => {
              console.log(
                "Service Worker registered with scope:",
                registration.scope,
              );
            })
            .catch((error) => {
              console.error("Service Worker registration failed:", error);
            });
        });
      }

      let mounted = true;
      const checkRedirect = async () => {
        try {
          const result = await webGetRedirectResult(auth);
          const user = result?.user ?? auth.currentUser;
          if (user) {
            // Sign-in via redirect just landed. If there's no VehiLink doc
            // yet, create one — same shape /login and /signup write when
            // they call the helper. Both screens show terms text next to
            // the OAuth buttons, so termsAccepted=true is justified. The
            // gate then routes the user normally (/onboarding for
            // not-yet-completed; /(tabs)/groups for completed).
            try {
              await ensureUserDocExists(firestore, user, {
                source: "redirect-return",
              });
            } catch (helperErr) {
              console.warn(
                "RootLayout: ensureUserDocExists failed; leaving user signed in for retry from the auth screen",
                helperErr,
              );
            }
          }
        } catch (err) {
          console.warn("RootLayout: getRedirectResult errored:", err);
        } finally {
          if (mounted) setRedirectChecked(true);
        }
      };
      checkRedirect();
      return () => {
        mounted = false;
      };
    } else {
      setRedirectChecked(true);
    }
  }, []);

  if (!redirectChecked) {
    return null;
  }

  return (
    <ErrorBoundary>
      <AuthProvider>
        <InitialLayout />
      </AuthProvider>
    </ErrorBoundary>
  );
}
